import { useCallback, useEffect, useRef, useState } from 'react'

export const useHorizontalScroll = () => {
  const [scrollDirection, setScrollDirection] = useState<'left' | 'right'>(
    'right',
  )
  const ref = useRef<HTMLDivElement>(null)
  const prevScrollLeft = useRef(0) // Store the previous scrollLeft value

  useEffect(() => {
    const element = ref.current
    const handleScroll = () => {
      if (element) {
        const { scrollLeft } = element

        if (scrollLeft > prevScrollLeft.current) {
          setScrollDirection('right')
        } else {
          setScrollDirection('left')
        }

        prevScrollLeft.current = scrollLeft
      }
    }

    if (element) {
      element.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (element) {
        element.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  return { scrollDirection, ref }
}

const SCROLL_DEBOUNCE_MS = 50

export const useDebouncedCallback = (
  cb: (() => void) | undefined,
  delay = SCROLL_DEBOUNCE_MS,
) => {
  const timer = useRef<NodeJS.Timeout | null>(null)

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [timer],
  )

  return useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => cb?.(), delay)
  }, [cb, delay])
}
